<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { convertToAttributeSlug } from 'dashboard/helper/commons.js';
import { ATTRIBUTE_MODELS, ATTRIBUTE_TYPES } from './constants';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

export default {
  components: {
    NextButton,
    Checkbox,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    // Passes 0 or 1 based on the selected AttributeModel tab selected in the UI
    // Needs a better data type, todo: refactor this component later
    selectedAttributeModelTab: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      // Using the prop as default. There is no side effect here as the component
      // is destroyed completely when the modal is closed. The prop doesn't change
      // dynamically when the modal is active.
      attributeModel: this.selectedAttributeModelTab || 0,
      attributeType: 0,
      attributeKey: '',
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      values: [],
      options: [],
      show: true,
      isTouched: false,
      draggedIndex: null,
      syncWithLabels: false,
      defaultValue: '',
      wonValue: '',
      isDealAmount: false,
    };
  },

  computed: {
    ...mapGetters({
      uiFlags: 'getUIFlags',
    }),
    models() {
      return ATTRIBUTE_MODELS.map(item => ({
        label: this.$t(`ATTRIBUTES_MGMT.ADD.FORM.MODEL.${item.NAME}.LABEL`),
        id: item.ID,
      }));
    },
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        label: this.$t(`ATTRIBUTES_MGMT.ADD.FORM.TYPE.${item.NAME}`),
        id: item.ID,
      }));
    },
    isMultiselectInvalid() {
      return this.isTouched && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    attributeListValues() {
      return this.values.map(item => item.name);
    },
    defaultValueOptions() {
      return this.attributeListValues.filter(
        value => value.toLocaleLowerCase().trim() !== 'sin seleccionar'
      );
    },
    isButtonDisabled() {
      return (
        this.v$.displayName.$invalid ||
        this.v$.description.$invalid ||
        this.uiFlags.isCreating ||
        this.isTagInputInvalid
      );
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    canSyncWithLabels() {
      return this.attributeModel === 1 && this.isAttributeTypeList;
    },
    canConfigureWonValue() {
      const key = (
        this.attributeKey ||
        convertToAttributeSlug(this.displayName) ||
        ''
      ).toLowerCase();
      return (
        this.attributeModel === 1 &&
        this.isAttributeTypeList &&
        key === 'estado_contacto'
      );
    },
    // El monto del negocio solo puede vivir en un atributo numerico de contacto
    canConfigureDealAmount() {
      return this.attributeModel === 1 && [1, 2].includes(this.attributeType);
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
  },

  validations: {
    displayName: {
      required,
      minLength: minLength(1),
    },
    description: {
      required,
    },
    attributeModel: {
      required,
    },
    attributeType: {
      required,
    },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },

  watch: {
    canSyncWithLabels(canSync) {
      if (!canSync) this.syncWithLabels = false;
    },
    // Si el tipo deja de ser numerico, el atributo ya no puede ser el monto
    canConfigureDealAmount(canConfigure) {
      if (!canConfigure) this.isDealAmount = false;
    },
    syncWithLabels(isEnabled) {
      if (!isEnabled) this.defaultValue = '';
    },
    defaultValueOptions(values) {
      if (this.defaultValue && !values.includes(this.defaultValue)) {
        this.defaultValue = '';
      }
      if (this.wonValue && !values.includes(this.wonValue)) {
        this.wonValue = '';
      }
    },
  },

  methods: {
    addTagValue(tagValue) {
      const tag = {
        name: tagValue,
      };
      this.values.push(tag);
      this.$refs.tagInput.$el.focus();
    },
    onTouch() {
      this.isTouched = true;
    },
    onDisplayNameChange() {
      this.attributeKey = convertToAttributeSlug(this.displayName);
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
    async addAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/create', {
          attribute_display_name: this.displayName,
          attribute_description: this.description,
          attribute_model: this.attributeModel,
          attribute_display_type: this.attributeType,
          attribute_key: this.attributeKey,
          attribute_values: this.attributeListValues,
          regex_pattern: this.regexPattern
            ? new RegExp(this.regexPattern).toString()
            : null,
          regex_cue: this.regexCue,
          sync_with_labels: this.syncWithLabels,
          default_value: this.defaultValue || null,
          won_value: this.wonValue || null,
          is_deal_amount: this.canConfigureDealAmount && this.isDealAmount,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.ADD.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.ADD.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index);
    },
    onDragEnter(index) {
      if (this.draggedIndex === null || this.draggedIndex === index) return;
      const temp = this.values[this.draggedIndex];
      this.values.splice(this.draggedIndex, 1);
      this.values.splice(index, 0, temp);
      this.draggedIndex = index;
    },
    onDragEnd() {
      this.draggedIndex = null;
    },
    removeOption(index) {
      this.values.splice(index, 1);
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="$t('ATTRIBUTES_MGMT.ADD.TITLE')" />

      <form class="flex w-full" @submit.prevent="addAttributes">
        <div class="w-full">
          <label :class="{ error: v$.attributeModel.$error }">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.LABEL') }}
            <select v-model="attributeModel">
              <option v-for="model in models" :key="model.id" :value="model.id">
                {{ model.option }}
              </option>
            </select>
            <span v-if="v$.attributeModel.$error" class="message">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.MODEL.ERROR') }}
            </span>
          </label>
          <woot-input
            v-model="displayName"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
            type="text"
            :class="{ error: v$.displayName.$error }"
            :error="
              v$.displayName.$error
                ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
                : ''
            "
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @update:model-value="onDisplayNameChange"
            @blur="v$.displayName.$touch"
          />
          <woot-input
            v-model="attributeKey"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
            type="text"
            :class="{ error: v$.attributeKey.$error }"
            :error="v$.attributeKey.$error ? keyErrorMessage : ''"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
            @blur="v$.attributeKey.$touch"
          />
          <label :class="{ error: v$.description.$error }">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
            <textarea
              v-model="description"
              rows="3"
              type="text"
              :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
              @blur="v$.description.$touch"
            />
            <span v-if="v$.description.$error" class="message">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
            </span>
          </label>
          <label :class="{ error: v$.attributeType.$error }">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
            <select v-model="attributeType">
              <option v-for="type in types" :key="type.id" :value="type.id">
                {{ type.option }}
              </option>
            </select>
            <span v-if="v$.attributeType.$error" class="message">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
            </span>
          </label>
          <div v-if="isAttributeTypeList" class="multiselect--wrap">
            <label>
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.LABEL') }}
            </label>
            <multiselect
              ref="tagInput"
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              label="name"
              track-by="name"
              :class="{ invalid: isMultiselectInvalid }"
              :options="options"
              multiple
              taggable
              @close="onTouch"
              @tag="addTagValue"
            />
            <label
              v-show="isMultiselectInvalid"
              class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
            >
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
            </label>
            <label
              v-if="canSyncWithLabels"
              class="flex items-center gap-2 mt-3 text-sm text-n-slate-12"
            >
              <Checkbox v-model="syncWithLabels" />
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.SYNC_WITH_LABELS') }}
            </label>
            <label v-if="canSyncWithLabels && syncWithLabels" class="mt-3">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.DEFAULT_VALUE') }}
              <select v-model="defaultValue">
                <option value="">
                  {{
                    $t(
                      'ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.DEFAULT_VALUE_PLACEHOLDER'
                    )
                  }}
                </option>
                <option
                  v-for="value in defaultValueOptions"
                  :key="value"
                  :value="value"
                >
                  {{ value }}
                </option>
              </select>
            </label>
            <label v-if="canConfigureWonValue" class="mt-3">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.WON_VALUE') }}
              <select v-model="wonValue">
                <option value="">
                  {{
                    $t(
                      'ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.WON_VALUE_PLACEHOLDER'
                    )
                  }}
                </option>
                <option
                  v-for="value in defaultValueOptions"
                  :key="value"
                  :value="value"
                >
                  {{ value }}
                </option>
              </select>
            </label>
            <div v-if="values.length > 0" class="mt-4">
              <label
                class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 block"
              >
                Orden de las opciones (Arrastra para reordenar):
              </label>
              <div class="flex flex-col gap-2">
                <div
                  v-for="(item, index) in values"
                  :key="item.name"
                  draggable="true"
                  class="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md cursor-move hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  @dragstart="onDragStart($event, index)"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(index)"
                  @dragend="onDragEnd"
                  @drop="onDragEnd"
                >
                  <div class="flex items-center gap-3">
                    <span
                      class="text-slate-400 dark:text-slate-500 cursor-grab active:cursor-grabbing"
                    >
                      <svg
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 8h16M4 16h16"
                        />
                      </svg>
                    </span>
                    <span
                      class="text-sm text-slate-800 dark:text-slate-200 font-medium"
                    >
                      {{ item.name }}
                    </span>
                  </div>
                  <button
                    type="button"
                    class="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400 transition-colors p-1"
                    @click="removeOption(index)"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isAttributeTypeText">
            <input
              v-model="regexEnabled"
              type="checkbox"
              @input="toggleRegexEnabled"
            />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
          </div>
          <woot-input
            v-if="isAttributeTypeText && isRegexEnabled"
            v-model="regexPattern"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
            type="text"
            :placeholder="
              $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
            "
          />
          <woot-input
            v-if="isAttributeTypeText && isRegexEnabled"
            v-model="regexCue"
            :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
          />
          <label v-if="canConfigureDealAmount" class="mt-3">
            <input v-model="isDealAmount" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DEAL_AMOUNT.LABEL') }}
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DEAL_AMOUNT.HELP') }}
            </p>
          </label>
          <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
              @click.prevent="onClose"
            />
            <NextButton
              type="submit"
              :label="$t('ATTRIBUTES_MGMT.ADD.SUBMIT')"
              :disabled="isButtonDisabled"
            />
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}

.multiselect--wrap {
  margin-bottom: 1rem;
}

:deep(.multiselect) {
  margin-bottom: 0;
}

:deep(.multiselect__content-wrapper) {
  display: none;
}

:deep(.multiselect--active) .multiselect__tags {
  border-radius: 0.3125rem;
}
</style>
